// Added by Sejune Oh on 2020-03-17
// Copyright (c) Coreline Soft, All rights are reserved.
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
import React, { FC, useState, useEffect, useRef } from 'react'
import { createStyles, makeStyles } from '@material-ui/core'
import cn from 'classnames'

// Helper
import { TASK_EXIT_CODE, Task } from '@/helper/define/taskDefine'
import { getDateWithFormat, getTaskExecutionTime } from '@/helper/util'
import Intl from '@/helper/Intl'

// UI
import { TaskListStyleTypes } from '@/components/worklist/WebComponents/TaskListWrapper'
import LegacyAppIcon from '@/legacy-polymer/LegacyAppIcon'

// Color
import { COLOR_LIGTH_NAVY } from '@/styles/Theme'
const COLOR_RUNNING = '#A7D0FF'
const COLOR_CANCELED = '#E2CA89'
const COLOR_WAITING = '#B6D3BC'
const COLOR_DONE = '#96E243'
const COLOR_FAILED = '#E24542'

const ROW_HEIGHT = 42

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Interface
export interface TaskListProps {
  /** Task list */
  taskList: Task[]
  /** Callback after row selection */
  selectRowCallback?: Function
  /** 실행 시작 시간 문구 */
  colStartedAt?: string
  /** 실행시간 문구 */
  colExecutionTime?: string
  /** 프로세스 이름 문구 */
  colProcName?: string
  /** 실행자 문구 */
  colInvoker?: string
  /** 작업 상태 문구 */
  colTaskStatus?: string
  colRuleName?: string
  /** Selected Task's id */
  selectedTaskId?: string
  /** Task의 type 분류 (Auto) */
  typeAuto?: string
  /** Task의 type 분류 (Manual) */
  typeManual?: string
  /** 리스트 갱신 시각 (라벨) */
  labelUpdatedAt?: string
  /** WC용 Styles  */
  classes?: TaskListStyleTypes
  /** List 갱신 */
  onClickReload?: (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void
}

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * 선택한 Study의 Task List UI 입니다.
 */
const TaskList: FC<TaskListProps> = ({
  taskList,
  selectRowCallback,
  colStartedAt: colStartedAtParam,
  colProcName: colProcNameParam,
  colExecutionTime: colExecutionTimeParam,
  colInvoker: colInvokerParam,
  colTaskStatus: colTaskStatusParam,
  colRuleName: colRuleNameParam,
  typeAuto: typeAutoParam,
  typeManual: typeManualParam,
  labelUpdatedAt: labelUpdatedAtParam,
  selectedTaskId,
  onClickReload,
  classes: webComponentClassses,
  ...props
}) => {
  const classes = webComponentClassses
    ? webComponentClassses['tasklist']
    : makeStyles(() => createStyles({ ...customStyles }))()
  const tableWrapperRef = useRef<HTMLDivElement>(null)

  const [lastLoadedAt, setLastLoadedAt] = useState('-')
  const [selectedIndex, setSelectedIndex] = useState<number>()

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  // ! I18n
  const colStartedAt = colStartedAtParam || Intl.getIntlText({ id: 'htmljs_react.worklist.tasklist.col_started_at' })
  const colExecutionTime =
    colExecutionTimeParam || Intl.getIntlText({ id: 'htmljs_react.worklist.tasklist.col_execution_time' })
  const colProcName = colProcNameParam || Intl.getIntlText({ id: 'htmljs_react.worklist.tasklist.col_proc_name' })
  const colInvoker = colInvokerParam || Intl.getIntlText({ id: 'htmljs_react.worklist.tasklist.col_invoker' })
  const colRuleName =  colRuleNameParam || Intl.getIntlText({ id: 'htmljs_react.worklist.tasklist.col_rule_name' })
  const colTaskStatus = colTaskStatusParam || Intl.getIntlText({ id: 'htmljs_react.worklist.tasklist.col_task_status' })
  const labelUpdatedAt = labelUpdatedAtParam || Intl.getIntlText({ id: 'htmljs_react.worklist.tasklist.label_updated_at' })

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  // ! Evt handler
  const handleClickRow = (taskId: string, index: number) => {
    setSelectedIndex(index)
    if (selectRowCallback) selectRowCallback(taskId)
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (taskList.length > 1 && typeof selectedIndex === 'number') {
      let nextIndex: number = selectedIndex

      switch (event.keyCode) {
        case 38: // Up
          if (selectedIndex === 0) return
          nextIndex = selectedIndex! - 1
          break
        case 40: // Down
          if (selectedIndex === taskList.length - 1) return
          nextIndex = selectedIndex! + 1
          break
        default:
          return
      }
      handleClickRow(taskList[nextIndex].id!, nextIndex)

      // 만약 Table의 Visible하지 않은 영역에 Cursor가 위치해 있는 경우 스크롤을 이동시킨다.
      const tableWrapper = tableWrapperRef.current!.getBoundingClientRect()
      if (tableWrapper.height > taskList.length * ROW_HEIGHT) return

      const tableRow = tableWrapperRef.current!.querySelector(`tr:nth-child(${nextIndex + 1})`)!.getBoundingClientRect()

      if (tableWrapper.top > tableRow.top - ROW_HEIGHT) {
        tableWrapperRef.current!.scrollTo(0, tableWrapperRef.current!.scrollTop - ROW_HEIGHT)
        return
      }

      if (tableWrapper.bottom < tableRow.bottom + ROW_HEIGHT) {
        tableWrapperRef.current!.scrollTo(0, tableWrapperRef.current!.scrollTop + ROW_HEIGHT)
        return
      }
    }
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  // ! Func
  const getDate = (date?: string) => {
    if (!date) return '-'

    return getDateWithFormat(date)
  }

  const getExecutionTime = (status?: string, procStartedAt?: string, procCompletedAt?: string) => {
    if (!status || status === 'RUNNING' || status === 'WAITING' || status === 'RETRYING') return '-'
    return getTaskExecutionTime(procStartedAt, procCompletedAt)
  }

  const getStatusIcon = (status?: string, exitCode?: number) => {
    switch (status) {
      case 'RUNNING':
        return (
          <LegacyAppIcon
            iconSize={20}
            iconName={'cl:aps-running'}
            title="Running"
            style={{ fill: COLOR_RUNNING, opacity: '0.8' }}
          />
        )
      case 'CANCELED':
        return (
          <LegacyAppIcon
            iconSize={20}
            iconName={'cl:aps-canceled'}
            title="Canceled"
            style={{ fill: COLOR_CANCELED, opacity: '0.8' }}
          />
        )
      case 'WAITING':
        return (
          <LegacyAppIcon
            iconSize={20}
            iconName={'cl:aps-waiting'}
            title="Waiting"
            style={{ fill: COLOR_WAITING, opacity: '0.8' }}
          />
        )
      case 'RETRYING':
        return (
          <LegacyAppIcon
            iconSize={20}
            iconName={'cl:aps-waiting'}
            title="Retrying"
            style={{ fill: COLOR_WAITING, opacity: '0.8' }}
          />
        )
      case 'DONE':
        return (
          <LegacyAppIcon
            iconSize={20}
            iconName={'cl:aps-done'}
            title="Done"
            style={{ fill: COLOR_DONE, opacity: '0.8' }}
          />
        )
      case 'FAILED':
        if (!Object.hasOwnProperty.call(TASK_EXIT_CODE, exitCode + '')) {
          return (
            <LegacyAppIcon
              iconSize={20}
              iconName={'cl:aps-crashed'}
              title="Crashed"
              style={{ fill: COLOR_FAILED, opacity: '0.8' }}
            />
          )
        }

        return (
          <LegacyAppIcon
            iconSize={20}
            iconName={'cl:aps-failed'}
            title="Failed"
            style={{ fill: COLOR_FAILED, opacity: '0.8' }}
          />
        )
      default:
        return '-'
    }
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  // ! Effects
  useEffect(() => {
    if (taskList.length === 0) setLastLoadedAt('-')
    else setLastLoadedAt(getDateWithFormat(new Date().toISOString(), false, true))
  }, [taskList])

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  // ! Render
  return (
    <div className={classes.section} tabIndex={0} onKeyDown={handleKeyDown}>
      {/* -------------- TABLE START -------------- */}
      <div className={classes.tableWrapper} ref={tableWrapperRef}>
        <table className={classes.table}>
          <colgroup>
            <col style={{ width: '15%' }} />
            <col style={{ width: '20%' }} />
            <col style={{ width: '20%' }} />
            <col style={{ width: '8%' }} />
            <col style={{ width: '17%' }} />
            <col style={{ width: '20%' }} />
          </colgroup>
          <thead>
            <tr>
              <th title={colInvoker}>{colInvoker}</th>
              <th title={colProcName}>{colProcName}</th>
              <th title={colRuleName}>{colRuleName}</th>
              <th title={colTaskStatus}>{colTaskStatus}</th>
              <th title={colExecutionTime}>{colExecutionTime}</th>
              <th title={colStartedAt}>{colStartedAt}</th>
            </tr>
          </thead>
          <tbody>
            {taskList.map(
              ({ id, user_display_name, status, proc_name, proc_started_at, proc_completed_at, proc_exit_code, rule_name }, index) => (
                <tr
                  key={`single-task-${id}`}
                  className={cn({ selected: selectedTaskId === id })}
                  onClick={() => handleClickRow(id!, index)}
                >
                  <td title={user_display_name || '-'}>{user_display_name || '-'}</td>
                  <td title={proc_name || '-'}>{proc_name || '-'}</td>
                  <td title={rule_name || '-'}>{rule_name || '-'}</td>
                  <td title={status}>{getStatusIcon(status, proc_exit_code)}</td>
                  <td title={getExecutionTime(status, proc_started_at, proc_completed_at)}>{getExecutionTime(status, proc_started_at, proc_completed_at)}</td>
                  <td title={getDate(proc_started_at)}>{getDate(proc_started_at)}</td>
                </tr>
              )
            )}
          </tbody>
        </table>
      </div>
      {/* -------------- TABLE END -------------- */}

      {/* -------------- TIMESTAMP START -------------- */}
      <div className={classes.timeWrapper}>
        <div className={classes.time}>
          {labelUpdatedAt} <strong>{lastLoadedAt}</strong>
        </div>
        <div className={classes.reloadBtn} tabIndex={1} onClick={onClickReload}>
          <LegacyAppIcon iconSize={20} iconName={'cl:refresh'} title="Reload" />
        </div>
      </div>
      {/* -------------- TIMESTAMP END -------------- */}
    </div>
  )
}

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// ! Style
export const customStyles: Record<string, any> = {
  section: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    height: '100%',
    backgroundColor: COLOR_LIGTH_NAVY,
    padding: '4px',
    outline: 'none'
  },
  tableWrapper: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    maxHeight: 'calc(100% - 21px)',
    background: '#000',
    border: '1px solid #000',
    overflow: 'hidden auto',
    '&::-webkit-scrollbar-thumb': {
      width: '50px',
      height: '50px',
      borderRadius: '4px',
      background: '#646464',
    },
    '&::-webkit-scrollbar': {
      width: '8px',
      height: '8px',
    },
  },
  table: {
    width: '100%',
    whiteSpace: 'nowrap',
    tableLayout: 'fixed',
    border: 0,
    borderCollapse: 'separate',
    borderSpacing: 0,
    '& th': {
      position: 'sticky',
      top: 0,
      zIndex: 1,
      backgroundColor: '#383838',
      borderBottom: '1px solid #000',
      paddingLeft: '8px',
      textAlign: 'left',
      '&:before': {
        content: '""',
        position: 'absolute',
        background: '#f00',
        right: 0,
        top: '15%',
        bottom: '15%',
        width: '2px',
        backgroundColor: '#2c2d2f',
      },
      '&:last-of-type:before': {
        content: 'none',
      },
    },
    '& tr': {
      height: ROW_HEIGHT,
      backgroundColor: '#1B1B1B',
      boxSizing: 'border-box',
    },
    '& > thead': {
      color: '#e6e6e6',
      top: 0,
      '& > tr': {
        height: '31px',
      },
    },
    '& > tbody': {
      '& tr': {
        cursor: 'pointer',
        color: '#b4b4b4',
        '& > td': {
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          borderTop: '1px solid transparent',
          borderBottom: '1px solid #383838',
        },
        '&.selected': {
          color: '#fff',
          backgroundColor: '#516276',
          '& > td': {
            borderTop: '1px solid #A7D0FF',
            borderBottom: '1px solid #A7D0FF',
            '&:first-of-type': {
              borderLeft: '1px solid #A7D0FF',
            },
            '&:last-of-type': {
              borderRight: '1px solid #A7D0FF',
            },
          },
        },
        '&:hover': {
          color: '#A7D0FF',
        },
      },
      '& td': {
        paddingLeft: '8px',
      },
    },
  },
  timeWrapper: {
    height: '21px',
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    padding: '6px 0 3px 0',
  },
  time: {
    marginRight: '8px',
    color: '#c8c8c8',
    '& > strong': {
      color: '#fff',
      fontWeight: 'normal',
    },
  },
  reloadBtn: {
    cursor: 'pointer',
    outline: 'none',
    '& > div': {
      transition: 'all ease 0.336s',
    },
    '&:hover > div': {
      fill: '#32a7d2',
      transform: 'rotate(180deg)',
    },
    '&:focus': {
      backgroundColor: 'rgb(17, 64, 97)'
    },
    '&:active': {
      backgroundColor: 'rgb(19, 80, 119)',
    },
  },
}

export default TaskList
